import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@shootsta/common-react';

interface CollapsibleTextInputProps {
  defaultValue: string;
  placeholder: string;
  onBlur: (args: { value: string }) => void;
  disabled?: boolean;
  id?: string;
}

function getCaretOffsetFromPoint(x: number, y: number): number | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (
      cx: number,
      cy: number
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (cx: number, cy: number) => Range | null;
  };

  // Only trust the offset when the click resolved to a text node. Clicks on the
  // preview's padding or the empty space below short text resolve to an element
  // node, whose offset is a child-node index (not a character offset) — return
  // null so the caller falls back to placing the caret at the end of the text.
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(x, y);
    return pos && pos.offsetNode.nodeType === Node.TEXT_NODE
      ? pos.offset
      : null;
  }

  if (typeof doc.caretRangeFromPoint === 'function') {
    const range = doc.caretRangeFromPoint(x, y);
    return range && range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startOffset
      : null;
  }

  return null;
}

function CollapsibleTextInput({
  defaultValue,
  placeholder,
  onBlur,
  disabled = false,
  id
}: CollapsibleTextInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pendingCaretRef = useRef<number | null>(null);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (isEditing && wrapperRef.current) {
      const textarea = wrapperRef.current.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        const len = textarea.value.length;
        const caret = pendingCaretRef.current;
        const offset = caret == null ? len : Math.min(caret, len);
        textarea.setSelectionRange(offset, offset);
      }
    }
    pendingCaretRef.current = null;
  }, [isEditing]);

  const handleBlur = useCallback(
    (blurArgs: { value: string }) => {
      setValue(blurArgs.value);
      setIsEditing(false);
      onBlur(blurArgs);
    },
    [onBlur]
  );

  const handleEdit = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
    }
  }, [disabled]);

  const handlePreviewMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }
      pendingCaretRef.current = getCaretOffsetFromPoint(e.clientX, e.clientY);
    },
    [disabled]
  );

  if (isEditing) {
    return (
      <div ref={wrapperRef}>
        <Input
          multiline
          rows={3}
          maxRows={3}
          defaultValue={value}
          placeholder={placeholder}
          onBlur={handleBlur}
          disabled={disabled}
          className="shot-v3__input [&_textarea]:resize-none [&_textarea]:!overflow-y-auto"
          id={id}
        />
      </div>
    );
  }

  return (
    <div
      className={`shot-v3__input h-[87px] overflow-hidden px-3 py-2 border border-russian rounded bg-white text-sm leading-6 ${
        disabled ? '' : 'cursor-text'
      }`}
      onMouseDown={handlePreviewMouseDown}
      onClick={handleEdit}
      onFocus={handleEdit}
      role="textbox"
      tabIndex={disabled ? -1 : 0}
    >
      {value ? (
        <span className="line-clamp-3 whitespace-pre-wrap break-words">
          {value}
        </span>
      ) : (
        <span className="text-regent">{placeholder}</span>
      )}
    </div>
  );
}

export default memo(CollapsibleTextInput);
